import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Paper, Chip, Skeleton, Snackbar, Alert, TextField, Button, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Menu, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ReportService from '../../../api/services/reportService';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import ReplayIcon from '@mui/icons-material/Replay';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { formatCurrency } from '../../../utils/currency';
import { standardFilterInputSx, standardFilterSelectSx, standardResetButtonSx, standardSecondaryButtonSx } from '../../../components/common/tableStyles';

export default function RateVarianceReportTab({ tenantId, isActive }) {
    const [reportData, setReportData] = useState([]);
    const [summary, setSummary] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        reasonCategory: ''
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        if (isActive) {
            fetchData();
        }
    }, [isActive, tenantId, filters, page, rowsPerPage]);

    const fetchData = async (isExport = false) => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { ...filters };
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === undefined) delete params[key];
            });
            
            if (isExport) {
                params.noPaging = true;
            } else {
                params.page = page + 1;
                params.limit = rowsPerPage;
            }
            
            const res = await ReportService.getRateVarianceReport(tenantId, params);
            const dataPayload = res?.data || res;
            
            // Handle standard pagination API format { status: "success", data: [...], pagination: {...} }
            const items = Array.isArray(dataPayload) ? dataPayload : (dataPayload?.data || dataPayload?.overrides || dataPayload?.items || []);
            
            // Extract pagination total count if provided (supports totalCount, total, etc.)
            const paginationTotal = res?.pagination?.totalCount ?? res?.pagination?.total ?? res?.totalCount ?? res?.total ?? res?.meta?.totalCount ?? res?.meta?.total;
            if (paginationTotal !== undefined) {
                setTotalCount(paginationTotal);
            } else {
                setTotalCount(items.length >= rowsPerPage ? -1 : items.length);
            }
            
            setReportData(items);
            
            // Calculate dynamic summary since the backend doesn't provide it
            if (items.length > 0) {
                const totalOverrides = items.length;
                const totalVariance = items.reduce((acc, curr) => acc + Number(curr.varianceAmount || 0), 0);
                const avgDiscount = items.reduce((acc, curr) => acc + Number(curr.discountPercentage || 0), 0) / totalOverrides;
                const pending = items.filter(i => (i.status || '').toLowerCase() === 'pending').length;
                
                setSummary({
                    totalOverrides,
                    totalVarianceAmount: totalVariance,
                    averageDiscountPercentage: avgDiscount,
                    pendingApprovals: pending
                });
            } else {
                setSummary(null);
            }
        } catch (err) {
            console.error('Error fetching rate variance report:', err);
            setError('Failed to load rate variance report. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPage(0);
    };

    const resetFilters = () => {
        setFilters({
            startDate: '',
            endDate: '',
            reasonCategory: ''
        });
        setPage(0);
    };

    const handleExportClick = (event) => {
        setExportAnchorEl(event.currentTarget);
    };

    const handleExportClose = () => {
        setExportAnchorEl(null);
    };

    const handleExport = async (format) => {
        handleExportClose();
        setIsExporting(true);
        setError(null);
        try {
            const params = { ...filters, format: 'csv' };
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === undefined) delete params[key];
            });
            const res = await ReportService.exportRateVarianceReport(tenantId, params);
            
            let csvText = '';
            if (res.data instanceof Blob) {
                csvText = await res.data.text();
            } else if (typeof res.data === 'string') {
                csvText = res.data;
            } else {
                csvText = JSON.stringify(res.data);
            }
            
            const rows = csvText.split('\n')
                .map(row => row.split(',').map(cell => cell.replace(/"/g, '').trim()))
                .filter(row => row.length > 0 && row.some(cell => cell !== ''));
                
            const fileName = `Rate_Variance_Report_${new Date().toISOString().split('T')[0]}`;
            
            if (format === 'csv') {
                const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${fileName}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            } else if (format === 'excel') {
                const ws = XLSX.utils.aoa_to_sheet(rows);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "RateVariance");
                XLSX.writeFile(wb, `${fileName}.xlsx`);
            } else if (format === 'pdf') {
                const doc = new jsPDF();
                const headers = rows[0] || [];
                const body = rows.slice(1) || [];
                
                doc.text("Rate Variance Report", 14, 15);
                autoTable(doc, {
                    head: [headers],
                    body: body,
                    startY: 20,
                    styles: { fontSize: 8, cellPadding: 2 },
                    headStyles: { fillColor: [63, 81, 181] }
                });
                doc.save(`${fileName}.pdf`);
            }
        } catch (err) {
            console.error(`Error exporting report as ${format}:`, err);
            setError(`Failed to export ${format.toUpperCase()} report. Please try again.`);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Box>
            <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="warning" onClose={() => setError(null)}>{error}</Alert>
            </Snackbar>

            <Paper sx={{ p: 2, mb: 3, borderRadius: '12px', border: '1px solid #edf1f6', bgcolor: '#ffffff', boxShadow: 'none' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        <TextField
                            label="Start Date"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                            sx={standardFilterInputSx}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <TextField
                            label="End Date"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                            sx={standardFilterInputSx}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <FormControl size="small" fullWidth>
                            <Select
                                value={filters.reasonCategory}
                                displayEmpty
                                onChange={(e) => handleFilterChange('reasonCategory', e.target.value)}
                                sx={standardFilterSelectSx}
                            >
                                <MenuItem value="">All Reason Categories</MenuItem>
                                <MenuItem value="Corporate Discount">Corporate Discount</MenuItem>
                                <MenuItem value="OTA Match">OTA Match</MenuItem>
                                <MenuItem value="Service Recovery">Service Recovery</MenuItem>
                                <MenuItem value="Management Approval">Management Approval</MenuItem>
                                <MenuItem value="Other">Other</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                    {(filters.startDate !== '' || filters.endDate !== '' || filters.reasonCategory !== '') && (
                        <Button variant="outlined" startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} sx={standardResetButtonSx} onClick={resetFilters} disabled={isExporting}>Reset</Button>
                    )}
                    <Button 
                        variant="outlined" 
                        onClick={handleExportClick}
                        disabled={isExporting}
                        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                        endIcon={<KeyboardArrowDownIcon />}
                        sx={standardSecondaryButtonSx}
                    >
                        {isExporting ? 'Exporting...' : 'Export Report'}
                    </Button>
                    <Menu anchorEl={exportAnchorEl} open={Boolean(exportAnchorEl)} onClose={handleExportClose}>
                        <MenuItem onClick={() => handleExport('csv')}>Export as CSV</MenuItem>
                        <MenuItem onClick={() => handleExport('excel')}>Export as Excel</MenuItem>
                        <MenuItem onClick={() => handleExport('pdf')}>Export as PDF</MenuItem>
                    </Menu>
                </Box>
            </Paper>

            {summary && !isLoading && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <Typography variant="h5" color="primary" fontWeight="bold">{summary.totalOverrides || 0}</Typography>
                            <Typography variant="body2" color="text.secondary">Total Overrides</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <Typography variant="h5" color="primary" fontWeight="bold">
                                {formatCurrency(summary.totalVarianceAmount || 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Total Variance Amount</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <Typography variant="h5" color="primary" fontWeight="bold">{Number(summary.averageDiscountPercentage || 0).toFixed(2)}%</Typography>
                            <Typography variant="body2" color="text.secondary">Avg Discount %</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <Typography variant="h5" color="primary" fontWeight="bold">{summary.pendingApprovals || 0}</Typography>
                            <Typography variant="body2" color="text.secondary">Pending Approvals</Typography>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Booking ID</TableCell>
                            <TableCell>Guest</TableCell>
                            <TableCell>Room</TableCell>
                            <TableCell align="right">Original Rate</TableCell>
                            <TableCell align="right">New Rate</TableCell>
                            <TableCell align="right">Variance</TableCell>
                            <TableCell>Reason</TableCell>
                            <TableCell>Requested By</TableCell>
                            <TableCell>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            Array.from(new Array(5)).map((_, idx) => (
                                <TableRow key={idx}>
                                    {Array.from(new Array(10)).map((_, colIdx) => (
                                        <TableCell key={colIdx}><Skeleton animation="wave" /></TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            reportData.length > 0 ? (
                                reportData.map((row, idx) => {
                                    const original = Number(row.originalRate || 0);
                                    const newRate = Number(row.overriddenRate || row.newRate || 0);
                                    const variance = Number(row.varianceAmount || (original - newRate));
                                    const variancePercent = Number(row.discountPercentage || (original > 0 ? (variance / original) * 100 : 0));
                                    
                                    // Parse reasonText if it's JSON
                                    let displayReason = row.reasonCategory || 'Discount';
                                    let remarks = '';
                                    try {
                                        if (row.reasonText && row.reasonText.startsWith('{')) {
                                            const parsed = JSON.parse(row.reasonText);
                                            displayReason = parsed.reason || displayReason;
                                            remarks = parsed.remarks || '';
                                        } else if (row.reasonText) {
                                            remarks = row.reasonText;
                                        }
                                    } catch(e) {
                                        remarks = row.reasonText || '';
                                    }
                                    
                                    return (
                                        <TableRow key={idx} hover>
                                            <TableCell>{(row.createdAt || row.date) ? new Date(row.createdAt || row.date).toLocaleDateString() : '-'}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="bold" color="primary">{row.bookingId || '-'}</Typography>
                                            </TableCell>
                                            <TableCell>{row.guestName || '-'}</TableCell>
                                            <TableCell>{row.roomNumber || 'Unassigned'}</TableCell>
                                            <TableCell align="right">{formatCurrency(original)}</TableCell>
                                            <TableCell align="right">{formatCurrency(newRate)}</TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" color={variance > 0 ? "success.main" : "error.main"}>
                                                    {formatCurrency(variance)} ({variancePercent.toFixed(1)}%)
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{displayReason}</Typography>
                                                {remarks && <Typography variant="caption" color="text.secondary">{remarks}</Typography>}
                                            </TableCell>
                                            <TableCell>{typeof row.requestedBy === 'string' ? row.requestedBy : (row.requestedBy?.name || '-')}</TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={row.status || 'Active'} 
                                                    size="small" 
                                                    color={row.status === 'Approved' ? 'success' : row.status === 'Pending' ? 'warning' : row.status === 'Rejected' ? 'error' : 'default'} 
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                                        <Typography color="text.secondary">No rate variance records found for the selected criteria.</Typography>
                                    </TableCell>
                                </TableRow>
                            )
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={reportData.length === 0 ? 0 : totalCount}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>
        </Box>
    );
}
